import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import { ClientStatusCardViewModel } from "@finpay-development/shared-platform/lib/esm/shared/model/client-status-card";
import { patientSearchResult } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/models/patient";

import { RootState } from "../../state/root-reducer";
import { ClientSelect } from "../client-select";
import classes from "./global-search.module.css";
import { getPatientClientIds } from "./global-search-utils";

interface GlobalSearchClientFilterProps {
  value: ClientStatusCardViewModel[];
  onChange: (clients: ClientStatusCardViewModel[]) => void;
  /** Unfiltered results; when present, options are limited to clients linked to them. */
  results: patientSearchResult[];
}

const byName = (a: ClientStatusCardViewModel, b: ClientStatusCardViewModel) =>
  (a.clientName ?? "").localeCompare(b.clientName ?? "");

const selectAllClients = (state: RootState): ClientStatusCardViewModel[] =>
  state.implementationContext?.implementationSpecialistClient?.allClients || [];

/** clientId → clientName from the clients loaded into redux at app start. */
export const useClientNamesById = (): Map<number, string> => {
  const allClients = useSelector(selectAllClients);
  return useMemo(
    () => new Map(allClients.map((c) => [c.clientId, c.clientName])),
    [allClients]
  );
};

const GlobalSearchClientFilter = (props: GlobalSearchClientFilterProps) => {
  const { value, onChange, results } = props;

  const allClients = useSelector(selectAllClients);

  const options = useMemo(() => {
    if (results.length === 0) {
      return [...allClients].sort(byName);
    }
    const resultClientIds = new Set<number>();
    results.forEach((patient) =>
      getPatientClientIds(patient._source).forEach((id) => resultClientIds.add(id))
    );
    const selectedIds = new Set(value.map((c) => c.clientId));
    return allClients
      .filter((c) => resultClientIds.has(c.clientId) || selectedIds.has(c.clientId))
      .sort(byName);
  }, [allClients, results, value]);

  return (
    <div className={classes.filterRow} test-id="global-search-client-filter">
      <Typography variant="body2" className={classes.filterLabel}>
        Clients
      </Typography>
      <div className={classes.filterField}>
        <ClientSelect
          options={options}
          value={value}
          label=""
          placeholder={value.length === 0 ? "All clients" : ""}
          size="small"
          onChange={(_event, selected) =>
            onChange(selected as unknown as ClientStatusCardViewModel[])
          }
        />
      </div>
    </div>
  );
};

export default GlobalSearchClientFilter;
